import { SYLLABLES, MAX_SYLLABLE_LEN } from '../data/pinyinSyllables'

/** A parsed syllable. `tone` is 1-4, or 0 for neutral / unmarked. */
export interface Syllable {
  /** Toneless base, lowercase, ü normalised to `v`. */
  base: string
  tone: number
  /** What the user (or the seed data) actually wrote, for error display. */
  raw: string
}

/** Vowels carrying a tone diacritic, mapped to [plain vowel, tone]. */
const TONE_MARKS: Record<string, [string, number]> = {
  ā: ['a', 1], á: ['a', 2], ǎ: ['a', 3], à: ['a', 4],
  ē: ['e', 1], é: ['e', 2], ě: ['e', 3], è: ['e', 4],
  ī: ['i', 1], í: ['i', 2], ǐ: ['i', 3], ì: ['i', 4],
  ō: ['o', 1], ó: ['o', 2], ǒ: ['o', 3], ò: ['o', 4],
  ū: ['u', 1], ú: ['u', 2], ǔ: ['u', 3], ù: ['u', 4],
  ǖ: ['v', 1], ǘ: ['v', 2], ǚ: ['v', 3], ǜ: ['v', 4],
  ü: ['v', 0],
  ń: ['n', 2], ň: ['n', 3], ǹ: ['n', 4],
}

/**
 * 一 and 不 change tone depending on what follows. Seed data stores them as
 * spoken (yì jiā, bú guò), but a learner who types the dictionary tone is not
 * wrong about the word — so the grader treats these two bases as tone-exempt.
 */
export const SANDHI_EXEMPT: ReadonlySet<string> = new Set(['yi', 'bu'])

const PUNCT = /[，。！？；：、“”‘’（）《》〈〉【】…—·,.!?;:'"()\[\]{}<>~`@#$%^&*_+=|\\/-]/g

/**
 * A chunk of pinyin reduced to bare letters, with the tone for each letter
 * recorded positionally.
 *
 * Tones are tracked per letter rather than per chunk because a diacritic sits
 * on the vowel *inside* a syllable ("hǎo") while a typed digit trails it
 * ("hao3"). Collapsing both to a single trailing digit loses the tone of every
 * syllable but the last when the learner types without spaces ("wǒhǎo").
 */
interface LetterRun {
  letters: string
  /** tones[i] is the tone attached to letters[i], or 0 if none. */
  tones: number[]
  /**
   * terminal[i] is true when letters[i]'s tone came from a typed digit.
   *
   * This is the difference that makes unspaced input parseable. A digit is
   * written *after* a complete syllable, so it pins the syllable boundary:
   * in "jian4gong1" the 4 proves the syllable ends at n, ruling out the
   * otherwise-valid reading "jiang". A diacritic carries no such information,
   * since it sits on a vowel in the middle of its syllable.
   */
  terminal: boolean[]
}

/**
 * Fold a chunk to letters + positional tones. A tone digit attaches to the
 * letter it follows; a diacritic attaches to the letter it sits on.
 */
function toLetterRun(raw: string): LetterRun {
  let letters = ''
  const tones: number[] = []
  const terminal: boolean[] = []

  for (const ch of raw.toLowerCase()) {
    const mark = TONE_MARKS[ch]
    if (mark) {
      letters += mark[0]
      tones.push(mark[1])
      terminal.push(false)
      continue
    }

    if (ch >= '0' && ch <= '5') {
      // Tone 5 is a common ASCII spelling of the neutral tone.
      if (tones.length) {
        tones[tones.length - 1] = Number(ch) % 5
        terminal[terminal.length - 1] = true
      }
      continue
    }

    if (ch >= 'a' && ch <= 'z') {
      letters += ch
      tones.push(0)
      terminal.push(false)
    }
  }

  return { letters, tones, terminal }
}

/**
 * Segment a letter run into syllables, backtracking so the whole run parses.
 * Returns null when no complete parse exists.
 */
function segmentRun(run: LetterRun): Syllable[] | null {
  const { letters, tones, terminal } = run
  const failed = new Set<number>()

  const walk = (pos: number): Syllable[] | null => {
    if (pos >= letters.length) return []
    if (failed.has(pos)) return null

    const maxLen = Math.min(MAX_SYLLABLE_LEN, letters.length - pos)
    candidates: for (let len = maxLen; len >= 1; len--) {
      const end = pos + len
      const base = letters.slice(pos, end)
      if (!SYLLABLES.has(base)) continue

      // A syllable carries exactly one tone, so a span holding two marked
      // letters must really be two syllables ("xīān" is xi1 + an1). And a
      // digit-marked letter has to be the last letter of its syllable.
      let tone = 0
      let marks = 0
      for (let k = pos; k < end; k++) {
        if (terminal[k] && k !== end - 1) continue candidates
        if (tones[k] || terminal[k]) {
          marks += 1
          if (marks > 1) continue candidates
          tone = tones[k]
        }
      }

      const rest = walk(end)
      if (!rest) continue

      return [{ base, tone, raw: base }, ...rest]
    }

    failed.add(pos)
    return null
  }

  return walk(0)
}

/**
 * Parse pinyin from either seed data (space-separated, tone-marked) or a
 * learner's typing (any spacing, tone digits or marks, `v` or `u:` for ü).
 *
 * Unparseable chunks are not dropped — they come back as a single syllable with
 * an empty `base`, so the grader can show the learner exactly what it could not
 * read instead of silently scoring it as a miss.
 */
export function parsePinyin(input: string): Syllable[] {
  const chunks = input
    // `u:` is an ASCII stand-in for ü. Fold it before punctuation stripping,
    // which would otherwise eat the colon and leave a bare `u`.
    .replace(/u:/gi, 'v')
    .replace(PUNCT, ' ')
    .split(/\s+/)
    .map((c) => c.trim())
    .filter(Boolean)

  const out: Syllable[] = []
  for (const c of chunks) {
    const run = toLetterRun(c)
    if (!run.letters) continue

    const parsed = segmentRun(run)
    if (parsed && parsed.length) out.push(...parsed)
    else out.push({ base: '', tone: 0, raw: c })
  }
  return out
}

const TONE_VOWEL_PRIORITY = ['a', 'o', 'e', 'i', 'u', 'v']
const MARKED: Record<string, string[]> = {
  a: ['a', 'ā', 'á', 'ǎ', 'à'],
  o: ['o', 'ō', 'ó', 'ǒ', 'ò'],
  e: ['e', 'ē', 'é', 'ě', 'è'],
  i: ['i', 'ī', 'í', 'ǐ', 'ì'],
  u: ['u', 'ū', 'ú', 'ǔ', 'ù'],
  v: ['ü', 'ǖ', 'ǘ', 'ǚ', 'ǜ'],
}

/** Render a parsed syllable back with a tone diacritic, for display. */
export function toToneMarked(syl: Syllable): string {
  if (!syl.base) return syl.raw
  const { base, tone } = syl
  if (tone === 0) return base.replace(/v/g, 'ü')

  // Standard placement: 'iu' and 'ui' mark the second vowel; otherwise the
  // first vowel in a-o-e-i-u-ü priority order takes the mark.
  let idx = -1
  if (base.includes('iu')) idx = base.indexOf('iu') + 1
  else if (base.includes('ui')) idx = base.indexOf('ui') + 1
  else {
    for (const v of TONE_VOWEL_PRIORITY) {
      const at = base.indexOf(v)
      if (at !== -1) {
        idx = at
        break
      }
    }
  }
  if (idx === -1) return base.replace(/v/g, 'ü')

  const vowel = base[idx]
  const replacement = MARKED[vowel]?.[tone] ?? vowel
  return (base.slice(0, idx) + replacement + base.slice(idx + 1)).replace(/v/g, 'ü')
}

/** Display form: "wo3 hao3" → "wǒ hǎo". */
export function prettyPinyin(input: string): string {
  return parsePinyin(input).map(toToneMarked).join(' ')
}
